<template>
  <div class="material-library">
    <header class="workspace-header">
      <div class="workspace-heading">
        <h1 class="workspace-title">{{ $t('material.title') }}</h1>
        <p class="workspace-subtitle">{{ $t('material.subtitle') }}</p>
      </div>
      <button
        v-if="profileReady && !onboardingOpen"
        type="button"
        class="level-entry"
        :aria-label="$t('learnerProfile.entryLabel')"
        @click="navigateToView('Profile')"
      >
        <span class="entry-setup">{{ $t('learnerProfile.title') }}</span>
        <template v-if="levelParts.length">
          <span v-for="part in levelParts" :key="part.key" class="entry-part">
            <span class="entry-label">{{ $t(`learnerProfile.${part.key}`) }}</span>
            <span class="entry-level">{{ part.level }}</span>
          </span>
        </template>
        <span class="material-icons entry-chevron" aria-hidden="true">chevron_right</span>
      </button>
    </header>

    <ProfileOnboarding v-if="onboardingOpen" @complete="finishOnboarding" />

    <div v-else class="library-body" v-loading="loading" :element-loading-text="$t('materialCategory.loading')">
      <section v-if="recentSources.length" class="library-section">
        <h3 class="group-title">{{ $t('material.recentTitle') }}</h3>
        <div class="material-grid">
          <button
            v-for="item in recentItems"
            :key="item.key"
            class="material-card recent-card"
            :class="{ finished: item.finished }"
            type="button"
            :disabled="Boolean(openingSource)"
            @click="openRecent(item.source)"
          >
            <div class="card-image">
              <MaterialCover
                v-if="item.source.cover?.src"
                :src="item.source.cover.src"
                :alt="item.title"
                :artwork="item.source.cover.artwork"
              />
              <span v-else class="material-icons recent-cover-empty" aria-hidden="true">headphones</span>
              <span
                v-if="item.progress !== null"
                class="recent-track"
                role="progressbar"
                aria-valuemin="0"
                aria-valuemax="100"
                :aria-valuenow="Math.round(item.progress * 100)"
              >
                <span class="recent-fill" :style="{ width: `${item.progress * 100}%` }"></span>
              </span>
            </div>
            <div class="card-content recent-content">
              <h4>{{ item.title }}</h4>
              <span class="recent-meta">
                <span v-if="item.position" class="recent-position">{{ item.position }}</span>
                <span>{{ item.time }}</span>
              </span>
              <span class="recent-bottom">
                <span class="recent-status">
                  <span v-if="item.finished" class="material-icons" aria-hidden="true">check</span>
                  {{ item.status }}
                </span>
                <span class="recent-action">
                  {{ openingSource === item.source ? $t('material.recentOpening') : item.finished ? $t('material.recentAgain') : $t('material.recentContinue') }}
                  <span class="material-icons" aria-hidden="true">arrow_forward</span>
                </span>
              </span>
            </div>
          </button>
        </div>
        <p v-if="recentError" class="recent-error">{{ recentError }}</p>
      </section>

      <section v-for="group in categoryGroups" :key="group.type" class="library-section">
        <h3 class="group-title">{{ group.label }}</h3>
        <div class="material-grid">
          <button
            v-for="category in group.categories"
            :key="category.id"
            class="material-card"
            type="button"
            @click="handleCardClick(category)"
          >
            <div class="card-image">
              <MaterialCover :src="category.image" :alt="category.title" :artwork="category.sourceType === 'podcast'" />
            </div>
            <div class="card-content">
              <h4>{{ category.title }}</h4>
              <p>{{ category.description }}</p>
            </div>
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { useViews } from '@/composables/views'
import { useMaterials } from '@/composables/useMaterials'
import { useLearnerProfile } from '@/composables/useLearnerProfile'
import { useAuth } from '@/composables/auth'
import { StudySourceMissingError, StudySourceNotReadyError, resolveStudySource } from '@/composables/studySource'
import { readRecentStudySources } from '@/utils/lastStudySource'
import ProfileOnboarding from '../profile/ProfileOnboarding.vue'
import MaterialCover from './MaterialCover.vue'

export default {
  name: 'MaterialLibrary',
  components: { MaterialCover, ProfileOnboarding },
  setup() {
    const { handleViewChange, continueAfterOnboarding } = useViews()
    const { profile, profileReady, showOnboarding } = useLearnerProfile()
    const { user } = useAuth()
    return { navigateToView: handleViewChange, continueAfterOnboarding, profile, profileReady, showOnboarding, user }
  },
  data() {
    return {
      categories: [],
      onboardingOpen: false,
      loading: false,
      // 最近播放的两条，中途退出后从听到的那句接着练
      recentSources: [],
      openingSource: null,
      recentError: ''
    }
  },
  computed: {
    recentItems() {
      return this.recentSources.map(source => {
        const index = Number.isInteger(source.subtitleIndex) ? source.subtitleIndex : null
        const total = source.subtitleCount || 0
        const progress = index !== null && total ? (index + 1) / total : null
        const finished = progress === 1
        return {
          key: `${source.audioId}-${source.materialId || ''}`,
          source,
          title: source.audioName || this.$t('material.recentUntitled'),
          progress,
          finished,
          position: index === null
            ? ''
            : total
              ? this.$t('material.recentPosition', { index: index + 1, total })
              : this.$t('material.recentPositionOnly', { index: index + 1 }),
          status: finished
            ? this.$t('material.recentFinished')
            : progress !== null ? this.$t('material.recentPercent', { percent: Math.floor(progress * 100) }) : '',
          time: this.playedAgo(source.playedAt)
        }
      })
    },
    levelParts() {
      return [
        { key: 'listening', level: this.profile.listeningLevel },
        { key: 'speaking', level: this.profile.speakingLevel }
      ].filter(part => part.level)
    },
    categoryGroups() {
      return [
        { type: 'video', label: this.$t('material.videoSection') },
        { type: 'audio', label: this.$t('material.audioSection') }
      ]
        .map(group => ({
          ...group,
          categories: this.categories.filter(category => this.categoryMediaType(category) === group.type)
        }))
        .filter(group => group.categories.length)
    }
  },
  async created() {
    await this.loadCategories()
  },
  // 页面会被缓存，每次回到素材库都重新读一次最近播放
  activated() {
    this.refreshRecentSources()
  },
  watch: {
    showOnboarding: {
      immediate: true,
      handler(show) {
        if (show) this.onboardingOpen = true
      }
    },
    'user.uid': {
      immediate: true,
      handler() {
        this.refreshRecentSources()
      }
    },
    '$i18n.locale'() {
      this.loadCategories()
    }
  },
  methods: {
    finishOnboarding() {
      this.onboardingOpen = false
      this.continueAfterOnboarding(this.$t('audioPlayer.demoVideoName'))
    },
    categoryMediaType(category) {
      return category.sourceType === 'youtube' || category.id?.startsWith('youtube-') ? 'video' : 'audio'
    },
    getMaterialService() {
      const translate = (...args) => this.$t(...args)
      translate.locale = this.$i18n?.locale
      return useMaterials(translate)
    },
    async loadCategories() {
      this.loading = true
      try {
        const { fetchMaterialCategories } = this.getMaterialService()
        this.categories = await fetchMaterialCategories()
      } catch {} finally {
        this.loading = false
      }
    },
    refreshRecentSources() {
      this.recentSources = readRecentStudySources(this.user?.uid)
    },
    playedAgo(playedAt) {
      const minutes = Math.round((playedAt - Date.now()) / 60000)
      const format = new Intl.RelativeTimeFormat(this.$i18n?.locale, { numeric: 'auto', style: 'short' })
      if (minutes > -60) return format.format(minutes, 'minute')
      if (minutes > -60 * 24) return format.format(Math.round(minutes / 60), 'hour')
      return format.format(Math.round(minutes / 60 / 24), 'day')
    },
    async openRecent(source) {
      this.openingSource = source
      this.recentError = ''
      const translate = (...args) => this.$t(...args)
      translate.locale = this.$i18n?.locale
      try {
        const audio = await resolveStudySource(source, translate)
        this.navigateToView('AudioPlayer', {
          ...audio,
          useSmartSplit: false,
          isDemo: false,
          focusSentence: { text: source.sentence, index: source.subtitleIndex },
          returnView: 'Material'
        })
      } catch (error) {
        if (error instanceof StudySourceMissingError) this.recentError = this.$t('material.recentMissing')
        else if (error instanceof StudySourceNotReadyError) this.recentError = this.$t('material.recentFailed')
        else this.recentError = error?.message || this.$t('material.recentFailed')
      } finally {
        this.openingSource = null
      }
    },
    handleCardClick(category) {
      this.navigateToView('Material', { categoryId: category.id })
    }
  }
}
</script>

<style scoped>
.material-library {
  max-width: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
}

.library-body {
  min-height: 240px;
}

.level-entry {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  align-self: flex-start;
  min-height: 40px;
  padding: 0 var(--space-2) 0 var(--space-4);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-pill);
  background: var(--surface-panel);
  color: var(--ink-primary);
  font: inherit;
  cursor: pointer;
  transition: border-color 0.15s ease;
}

.level-entry:hover {
  border-color: var(--ink-tertiary);
}

.level-entry:focus-visible {
  outline: 2px solid var(--signal-active-strong);
  outline-offset: 2px;
}

.entry-part {
  margin-left: var(--space-3);
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
}

.entry-part + .entry-part {
  margin-left: var(--space-3);
  padding-left: var(--space-3);
  border-left: 1px solid var(--border-subtle);
}

.entry-label {
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
}

.entry-level {
  color: var(--ink-primary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-sm);
  font-weight: 600;
}

.entry-setup {
  color: var(--ink-primary);
  font-size: var(--font-size-sm);
  font-weight: 500;
}

.entry-chevron {
  margin-left: 2px;
  color: var(--ink-tertiary);
  font-size: 18px;
}

@media (prefers-reduced-motion: reduce) {
  .level-entry,
  .recent-action .material-icons {
    transition: none;
  }
}

.library-section + .library-section {
  margin-top: var(--space-8);
}

.group-title {
  margin: 0 0 var(--space-3);
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  font-weight: 600;
}

.recent-card:disabled {
  cursor: progress;
}

.recent-cover-empty {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  color: var(--ink-tertiary);
  font-size: 32px;
}

.recent-card.finished .card-image::after {
  content: '';
  position: absolute;
  inset: 0;
  background: var(--surface-panel);
  opacity: 0.45;
}

.recent-track {
  position: absolute;
  z-index: 1;
  left: 0;
  right: 0;
  bottom: 0;
  height: 4px;
  background: rgba(16, 24, 32, 0.25);
}

.recent-fill {
  display: block;
  height: 100%;
  background: var(--signal-active);
}

.recent-card.finished .recent-fill {
  background: var(--ink-tertiary);
}

.recent-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.recent-meta {
  display: flex;
  gap: var(--space-2);
  overflow: hidden;
  white-space: nowrap;
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
}

.recent-position {
  color: var(--ink-secondary);
  font-variant-numeric: tabular-nums;
}

.recent-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  margin-top: var(--space-2);
}

.recent-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
}

.recent-status .material-icons {
  font-size: 14px;
}

.recent-card.finished .recent-status {
  color: var(--color-success-text);
}

.recent-action {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: var(--ink-primary);
  font-size: 0.8125rem;
  font-weight: 500;
  white-space: nowrap;
}

.recent-card.finished .recent-action {
  color: var(--ink-secondary);
}

.recent-action .material-icons {
  font-size: 16px;
  transition: transform 0.18s ease;
}

.recent-card:hover:not(:disabled) .recent-action .material-icons {
  transform: translateX(2px);
}

.recent-error {
  margin: var(--space-2) 0 0;
  color: var(--color-danger);
  font-size: var(--font-size-sm);
}

.material-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  grid-auto-rows: max-content;
  gap: var(--space-4);
}

.material-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-panel);
  text-align: left;
  font: inherit;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.material-card:hover {
  transform: translateY(-1px);
  border-color: var(--border-active);
  box-shadow: var(--shadow-lift);
}

.material-card:focus-visible {
  outline: 2px solid var(--signal-active-strong);
  outline-offset: 2px;
}

.card-image {
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: var(--surface-subtle);
}

.card-content {
  padding: var(--space-3);
}

.card-content h4 {
  margin: 0;
  overflow: hidden;
  color: var(--ink-primary);
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.35;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.card-content p {
  display: -webkit-box;
  min-height: calc(2 * 1.45em);
  margin: var(--space-1) 0 0;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: var(--ink-secondary);
  font-size: 0.8125rem;
  line-height: 1.45;
}

@media (prefers-reduced-motion: reduce) {
  .material-card {
    transition: none;
  }

  .material-card:hover {
    transform: none;
  }
}

@media (max-width: 768px) {
  .material-grid {
    grid-template-columns: 1fr;
    gap: var(--space-3);
  }

  .material-card {
    display: grid;
    grid-template-columns: 112px minmax(0, 1fr);
  }

  .card-image {
    aspect-ratio: auto;
    height: 100%;
    min-height: 84px;
  }
}
</style>
